/**
 * Workspace state (HANDOFF §0.16, mockup W1): one banner under the top bar at most, the day-13 modal once per
 * day, and nothing at all while the workspace is active. Monitoring, alerts and legal duties never stop.
 *
 * From `GET /me` → `currentTenant` (`status`, `trialEndsAt`, `readOnlyReason`, the plan's `limits`); the deletion
 * date from `GET /tenants/current` (`deletionDueAt`, any member) once the workspace is read-only.
 */
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useGetBillingSubscription, useGetCurrentTenant } from "@brevalt/api-client/customer";
import { Banner, Button, DialogContent, DialogRoot, useNow } from "@brevalt/ui";
import { isReadOnlyStatus, parseInstant, prefs, screenTime, trialDaysLeft, type Entitlements } from "@brevalt/app-core";

const BILLING = "/settings/billing";
/** Day 10 of 14: the banner from 4 days left (04 §14). */
const BANNER_FROM_DAYS = 4;

export type WorkspaceState =
  "active" | "trial-banner" | "trial-last-day" | "past-due" | "read-only" | "suspended" | "archived" | "deletion-due";

export function workspaceState(
  e: Pick<Entitlements, "tenantStatus" | "trialEndsAt">,
  deletionDueAt: string | null | undefined,
  now: Date,
): WorkspaceState {
  const status = e.tenantStatus;
  if (deletionDueAt && isReadOnlyStatus(status)) return "deletion-due";
  if (status === "past_due") return "past-due";
  if (status === "suspended") return "suspended";
  if (status === "archived") return "archived";
  if (isReadOnlyStatus(status)) return "read-only";
  if (status === "trial") {
    const days = trialDaysLeft(e.trialEndsAt, now);
    if (days !== null && days <= 1) return "trial-last-day";
    if (days !== null && days <= BANNER_FROM_DAYS) return "trial-banner";
  }
  return "active";
}

/** The calendar day of an instant in the viewer's zone ("2026-10-08"). */
export function localDay(instant: Date | string, zone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).format(
    parseInstant(instant),
  );
}

export function WorkspaceBanner({ entitlements, zone }: { entitlements: Entitlements; zone: string }) {
  const { t } = useTranslation();
  const now = useNow(undefined, 60_000);
  const readOnly = isReadOnlyStatus(entitlements.tenantStatus);
  const tenant = useGetCurrentTenant({ query: { enabled: readOnly, staleTime: 5 * 60_000 } });
  const state = workspaceState(entitlements, tenant.data?.deletionDueAt, now);
  const canBill = entitlements.can("billing.manage");
  // The date the payment failed: only members with billing.manage may read the subscription (REQUESTS #51).
  const subscription = useGetBillingSubscription({
    query: { enabled: state === "past-due" && canBill, staleTime: 5 * 60_000 },
  });
  // The day-13 modal: once per day of the viewer (re-checked every minute, so it comes back after midnight).
  const today = localDay(now, zone);
  const [dismissedOn, setDismissedOn] = useState(() => prefs.get("trialReminderDay"));
  const modal = state === "trial-last-day" && dismissedOn !== today;
  const owner = entitlements.can("workspace.delete");
  if (state === "active") return null;

  const plans = (
    <Button size="s" variant="primary" asChild>
      <a href={BILLING}>{t("shell.choose_a_plan")}</a>
    </Button>
  );
  const payment = canBill ? (
    <Button size="s" asChild>
      <a href={BILLING}>{t("shell.update_payment_method")}</a>
    </Button>
  ) : undefined;
  const date = (instant: string | null | undefined) => (instant ? screenTime(instant, zone).local : "");
  const days = trialDaysLeft(entitlements.trialEndsAt, now) ?? 0;
  // From the plan's limits; without them the sentence is left out (no guessed number).
  const readOnlyDays = entitlements.limits?.readOnlyDaysAfterTrial ?? null;
  const endsToday = !!entitlements.trialEndsAt && localDay(entitlements.trialEndsAt, zone) === today;
  const pastDueSince = subscription.data?.pastDueSince;

  let banner: ReactNode = null;
  switch (state) {
    case "trial-banner":
      banner = (
        <Banner tone="trial" title={t("shell.your_trial_ends_in_days", { days })} action={plans}>
          {t("shell.trial_ends_body", { date: date(entitlements.trialEndsAt) })}
          {readOnlyDays !== null && ` ${t("shell.nothing_deleted_for_days", { days: readOnlyDays })}`}
        </Banner>
      );
      break;
    case "trial-last-day":
      banner = (
        <Banner
          tone="trial"
          title={endsToday ? t("shell.your_trial_ends_today") : t("shell.your_trial_ends_tomorrow")}
          action={plans}
        >
          {readOnlyDays !== null
            ? t("shell.after_that_read_only_for_days", { days: readOnlyDays })
            : t("shell.after_that_read_only")}
        </Banner>
      );
      break;
    case "past-due":
      banner = (
        <Banner tone="warn" title={t("shell.the_last_payment_didn_t_go")} action={payment}>
          {pastDueSince && `${t("shell.payment_failed_on_date", { date: date(pastDueSince) })} `}
          {entitlements.readOnlyFrom
            ? t("shell.past_due_body_until", { date: date(entitlements.readOnlyFrom) })
            : t("shell.past_due_body")}
        </Banner>
      );
      break;
    case "read-only":
      banner = (
        <Banner tone="warn" title={t("shell.read_only_choose_a_plan_to")} action={plans}>
          {t("shell.your_legal_duties_still_work_cases")}
        </Banner>
      );
      break;
    case "suspended":
      banner = (
        <Banner tone="critical" title={t("shell.suspended_the_payment_is_overdue")} action={payment}>
          {t("shell.update_the_payment_method_to_make")}
        </Banner>
      );
      break;
    case "archived":
      banner = (
        <Banner tone="warn" title={t("shell.archive_only_read_and_export")} action={plans}>
          {t("shell.archived_body")}
        </Banner>
      );
      break;
    case "deletion-due":
      banner = (
        <Banner
          tone="critical"
          title={t("shell.workspace_deleted_on_date", { date: date(tenant.data?.deletionDueAt) })}
          action={
            <div className="flex flex-wrap gap-2">
              {plans}
              {owner ? (
                <Button size="s" asChild>
                  <a href="/settings/data">{t("shell.export_your_data")}</a>
                </Button>
              ) : (
                <>
                  {/* Export all is Owner-only (workspace.delete + step-up); others see the reason. */}
                  <Button size="s" disabled aria-describedby="export-owner">
                    {t("shell.export_your_data")}
                  </Button>
                  <span id="export-owner" className="self-center text-2xs text-text-muted">
                    {t("shell.the_owner_can_export_everything")}
                  </span>
                </>
              )}
            </div>
          }
        >
          {t("shell.deletion_due_body")}
        </Banner>
      );
      break;
  }

  const remindLater = () => {
    prefs.set("trialReminderDay", today);
    setDismissedOn(today);
  };
  return (
    <div className="border-b border-border bg-surface px-4 py-3 lg:px-8" data-workspace-state={state}>
      {banner}
      {state === "trial-last-day" && (
        <DialogRoot
          open={modal}
          onOpenChange={(open) => {
            if (!open) remindLater();
          }}
        >
          <DialogContent
            title={endsToday ? t("shell.your_trial_ends_today_title") : t("shell.your_trial_ends_tomorrow_2")}
            description={t("shell.choose_a_plan_now_and_nothing")}
            footer={
              <>
                <Button onClick={remindLater}>{t("shell.remind_me_later")}</Button>
                <Button variant="primary" asChild>
                  <a href={BILLING} onClick={remindLater}>
                    {t("shell.choose_a_plan")}
                  </a>
                </Button>
              </>
            }
          />
        </DialogRoot>
      )}
    </div>
  );
}
